import React from 'react'
import { ArrowUpRight, MapPin, User, Warehouse } from 'lucide-react'
import { Link } from 'react-router-dom'

const solutions = [
  {
    number: '01',
    title: 'D2C Logistics',
    subtitle: 'For Growing Brands',
    icon: MapPin,
    description:
      'Flexible freight, express delivery and warehousing solutions that help D2C brands scale faster and serve customers better.',
    link: '/services/ltl'
  },
  {
    number: '02',
    title: 'Personal Parcel',
    subtitle: 'Send With Confidence',
    icon: User,
    description:
      'Send packages, gifts and important documents with convenient doorstep pickup, real-time tracking and reliable delivery.',
    link: '/services/cl'
  },
  {
    number: '03',
    title: 'Smart Warehousing',
    subtitle: 'Smarter Supply Chains',
    icon: Warehouse,
    description:
      'Secure storage, inventory visibility and efficient order fulfilment to keep your business operations running smoothly.',
    link: '/services/warehousing'
  }
]

const BusinessSolutions = () => {
  return (
    <section className='relative isolate overflow-hidden bg-[#211f57] py-8 md:py-12'>
      {/* Subtle background detail */}
      <div className='pointer-events-none absolute right-0 top-0 h-full w-[35%] bg-gradient-to-l from-white/[0.025] to-transparent' />

      <div className='relative mx-auto max-w-[1350px] px-6 md:px-10'>
        {/* Heading */}
        <div className='mb-9 flex flex-col justify-between gap-5 md:mb-11 md:flex-row md:items-end'>
          <div>
            <h2 className='text-[32px] font-bold leading-[1.12] tracking-[-1.4px] text-white md:text-[43px]'>
              Logistics Made
              <span className='text-[#d32c30]'> Simpler.</span>
              <br />
              Business Made Stronger.
            </h2>
          </div>

          <p className='max-w-[315px] pb-1 text-[12px] leading-[1.9] text-white/55'>
            From individual shipments to growing enterprises, we create smarter
            ways to move, store and deliver.
          </p>
        </div>

        {/* Services */}
        <div className='grid grid-cols-1 border-y border-white/15 md:grid-cols-3'>
          {solutions.map((solution, index) => {
            const Icon = solution.icon

            return (
              <div
                key={solution.number}
                className={`group relative flex flex-col px-0 py-7 transition-colors duration-300 hover:bg-white/[0.035] md:min-h-[270px] md:px-7 md:py-8 ${
                  index !== 0
                    ? 'border-t border-white/15 md:border-l md:border-t-0'
                    : ''
                }`}
              >
                {/* Top row */}
                <div className='mb-8 flex items-center justify-between'>
                  <span className='text-[10px] font-semibold tracking-[2px] text-white/40'>
                    SERVICE / {solution.number}
                  </span>

                  <div className='flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-[#d32c30] transition-all duration-300 group-hover:border-[#d32c30] group-hover:bg-[#d32c30] group-hover:text-white'>
                    <Icon size={17} strokeWidth={1.6} />
                  </div>
                </div>

                {/* Main content */}
                <div className='flex-1'>
                  <span className='mb-2 block text-[9px] font-semibold uppercase tracking-[2px] text-[#ff7777]'>
                    {solution.subtitle}
                  </span>

                  <h3 className='mb-3 text-[22px] font-semibold tracking-[-0.6px] text-white transition-colors duration-300 group-hover:text-[#ff7777]'>
                    {solution.title}
                  </h3>

                  <p className='max-w-[340px] text-[11px] leading-[1.9] text-white/55'>
                    {solution.description}
                  </p>
                </div>

                {/* Service link */}
                <div className='mt-7 flex justify-end'>
                  <Link
                    to={solution.link}
                    className='group/link inline-flex items-center gap-2 border-b border-white/20 pb-1.5 text-[10px] font-semibold tracking-wide text-white/80 transition-all duration-300 hover:border-[#d32c30] hover:text-[#ff7777]'
                  >
                    Explore Service
                    <ArrowUpRight
                      size={14}
                      className='transition-transform duration-300 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5'
                    />
                  </Link>
                </div>

                {/* Hover indicator */}
                <span className='absolute bottom-0 left-0 h-[2px] w-0 bg-[#d32c30] transition-all duration-500 group-hover:w-full' />
              </div>
            )
          })}
        </div>

        {/* Footer line */}
        <div className='mt-2 flex flex-wrap items-center justify-between gap-3'>
          <span className='text-[9px] font-medium uppercase tracking-[2px] text-white/35'>
            One Partner. Every Logistics Need.
          </span>

          <Link
            to='/services'
            className='group inline-flex items-center gap-2 text-[10px] font-semibold text-[#ff7777] transition-colors hover:text-white'
          >
            Explore All Services
            <ArrowUpRight
              size={14}
              className='transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1'
            />
          </Link>
        </div>
      </div>
    </section>
  )
}

export default BusinessSolutions
